import React from 'react';
import { Link } from 'react-router-dom';
import { Line, Bar, Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const LeadInsightsChart = ({ data, dashboardData, dateRange, onDateChange }) => {
  const chartData = {
    labels: data?.dates || [],
    datasets: [
      {
        label: 'Leads',
        data: data?.query || [],
        borderColor: '#FBBF24',
        backgroundColor: 'rgba(251, 191, 36, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Unlocked',
        data: data?.buy || [],
        borderColor: '#58d60c',
        backgroundColor: 'rgba(88, 214, 12, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => Math.round(value)
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Lead Insights</h3>
            <Link
              to="/lead-analytics"
              className="inline-flex items-center rounded-md border border-[#009ef7] px-2.5 py-1 text-xs font-semibold text-[#009ef7] hover:bg-[#009ef7]/5 no-underline"
            >
              Lead Analytics
            </Link>
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-xs text-gray-600 break-words">
              <span className="font-semibold text-gray-900">{dashboardData?.todayLeads || 0}</span> Leads Today •{' '}
              <span className="font-semibold text-gray-900">{dashboardData?.todayUnlocked || 0}</span> Today Unlocked •{' '}
              <span className="font-semibold text-gray-900">{dashboardData?.todayTotalUnlock || 0}</span> Today Total Unlocked
            </div>
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => onDateChange('leadStart', e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-xs w-32 sm:w-auto"
              />
              <span className="text-xs text-gray-600">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => onDateChange('leadEnd', e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-xs w-32 sm:w-auto"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="p-0">
        <div className="bg-gray-50 p-2 rounded-lg" style={{ height: '350px' }}>
          <Line key={`lead-${dateRange.start}-${dateRange.end}`} data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export const UserVendorChart = ({ data, dashboardData, dateRange, onDateChange }) => {
  const chartData = {
    labels: data?.dates || [],
    datasets: [
      {
        label: 'Users',
        data: data?.user || [],
        borderColor: '#22C55E',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: false,
        tension: 0.4
      },
      {
        label: 'Vendors',
        data: data?.vendor || [],
        borderColor: '#00d4ff',
        backgroundColor: 'rgba(0, 212, 255, 0.1)',
        fill: false,
        tension: 0.4
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Users and Vendors</h3>
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-xs text-gray-600">
              👤 <span className="font-semibold text-gray-900">{dashboardData?.todayUsers || 0}</span> Users today •{' '}
              🧾 <span className="font-semibold text-gray-900">{dashboardData?.todayVendors || 0}</span> Vendors today
            </div>
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => onDateChange('userVendorStart', e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-xs w-32 sm:w-auto"
              />
              <span className="text-xs text-gray-600">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => onDateChange('userVendorEnd', e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-xs w-32 sm:w-auto"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="p-0">
        <div className="bg-gray-50 p-2 rounded-lg border border-gray-200" style={{ height: '350px' }}>
          <Line key={`uv-${dateRange.start}-${dateRange.end}`} data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export const OrdersChart = ({ data, dashboardData }) => {
  // Helper function to convert month to label
  const getMonthLabel = (month) => {
    // Check if month is in "YYYY-MM" format or just a number
    if (typeof month === 'string' && month.includes('-')) {
      const [year, monthNum] = month.split('-');
      const date = new Date(year, parseInt(monthNum) - 1);
      return date.toLocaleString('default', { month: 'short' });
    } else {
      const monthNum = parseInt(month);
      const date = new Date(2024, monthNum - 1);
      return date.toLocaleString('default', { month: 'short' });
    }
  };

  // Ensure data is an array
  const chartDataArray = Array.isArray(data) ? data : [];

  const chartData = {
    labels: chartDataArray.map(item => getMonthLabel(item.month)),
    datasets: [
      {
        label: 'Orders',
        data: chartDataArray.map(item => item.orders || 0),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        type: 'line',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        yAxisID: 'y1',
        order: 1
      },
      {
        label: 'Revenue',
        data: chartDataArray.map(item => item.revenue || 0),
        backgroundColor: '#22C55E',
        borderColor: '#22C55E',
        borderRadius: 6,
        order: 2
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              if (context.dataset.label === 'Revenue') {
                label += '₹' + context.parsed.y.toLocaleString();
              } else {
                label += context.parsed.y + ' orders';
              }
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        position: 'left',
        title: {
          display: true,
          text: 'Revenue (₹)',
          color: '#22C55E',
          font: {
            size: 13
          }
        },
        ticks: {
          color: '#22C55E',
          callback: function(value) {
            return '₹' + value.toLocaleString();
          }
        }
      },
      y1: {
        beginAtZero: true,
        position: 'right',
        title: {
          display: true,
          text: 'Orders',
          color: '#3B82F6',
          font: {
            size: 13
          }
        },
        ticks: {
          color: '#3B82F6'
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-2">
      <div className="p-3 border-b border-gray-200">
        <div className="flex flex-row sm:flex-row justify-between items-start sm:items-center gap-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">📈 Orders</h3>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-xs text-gray-600">Completed Orders
            <p className="text-lg font-semibold text-right" style={{ color: '#009ef7' }}>{dashboardData?.completedOrders || 0}</p></p>
          </div>
        </div>
      </div>
      <div className="p-2">
        <div style={{ height: '350px' }}>
          <Chart type='bar' data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export const UsersPerDayChart = ({ data, dashboardData, dateRange, onDateChange }) => {
  const chartData = {
    labels: data?.dates || [],
    datasets: [
      {
        label: 'Users',
        data: data?.user || [],
        backgroundColor: '#009ef7'
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col gap-1">
          <div className="flex sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">📈 Users Per Day</h3>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs text-gray-600">Today
              <p className="text-lg font-semibold text-right" style={{ color: '#009ef7' }}>{dashboardData?.todayUsers || 0}</p></p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => onDateChange('userStart', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs w-32 sm:w-auto"
            />
            <span className="text-xs text-gray-600">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => onDateChange('userEnd', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs w-32 sm:w-auto"
            />
          </div>
        </div>
      </div>
      <div className="p-2">
        <div style={{ height: '350px' }}>
          <Bar key={`user-${dateRange.start}-${dateRange.end}`} data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export const UnlockPerDayChart = ({ data, dashboardData, dateRange, onDateChange }) => {
  const chartData = {
    labels: data?.dates || [],
    datasets: [
      {
        label: 'Replacement',
        data: data?.totalReplacementMap || [],
        borderColor: '#EF4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Net Unlock',
        data: data?.netAmountMap || [],
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Total Unlock',
        data: data?.totalAmountMap || [],
        borderColor: '#FFD700',
        backgroundColor: 'rgba(255, 215, 0, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Unlock Transactions',
        data: data?.transactionMap || [],
        borderColor: '#009ef7',
        backgroundColor: 'rgba(0, 158, 247, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col gap-1">
          <div className="flex sm:flex-row justify-between items-start sm:items-center gap-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">📈 Unlock Per Day</h3>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs text-gray-600 text-right">Today <p className="text-lg font-semibold" style={{ color: '#009ef7' }}>{dashboardData?.totalDebitAmount || 0}</p></p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => onDateChange('unlockStart', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs w-32 sm:w-auto"
            />
            <span className="text-xs text-gray-600">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => onDateChange('unlockEnd', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs w-32 sm:w-auto"
            />
          </div>
        </div>
      </div>
      <div className="p-2">
        <div style={{ height: '350px' }}>
          <Line key={`unlock-${dateRange.start}-${dateRange.end}`} data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export const SourceAnalyticsChart = ({ data, dateRange, onDateChange }) => {
  const chartData = {
    labels: data?.dates || [],
    datasets: [
      {
        label: 'Google',
        data: data?.google || [],
        backgroundColor: '#EF4444'
      },
      {
        label: 'Meta',
        data: data?.meta || [],
        backgroundColor: '#10B981'
      },
      {
        label: 'Other',
        data: data?.other || [],
        backgroundColor: '#F59E0B'
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'center'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 mb-2">
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
              <i className="bi bi-bar-chart-line-fill mr-2" style={{ color: '#009ef7', fontSize: '1.3rem' }}></i>
              Lead Sources Analytics
            </h3>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => onDateChange('sourceStart', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs w-32 sm:w-auto"
            />
            <span className="text-xs text-gray-600 font-semibold">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => onDateChange('sourceEnd', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs w-32 sm:w-auto"
            />
          </div>
        </div>
      </div>
      <div className="p-0">
        <div className="bg-gray-50 rounded-lg p-2" style={{ height: '400px' }}>
          <Bar key={`source-${dateRange.start}-${dateRange.end}`} data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};


